/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ExtractedErrorText } from '@app/core/types';

const UNKNOWN_ERROR_MESSAGE = 'Unknown';

const hasErrorString = (error: unknown): error is { error: string } => {
  if (!error) return false;
  const message = (error as any).error;
  return typeof message === 'string' && message.length !== 0;
};

type CloudflareHttpError = { EdgeResponseStatus: number; CustomStatusMessage: string }[];

const isCloudflareError = (error: unknown): error is CloudflareHttpError => {
  return (
    Array.isArray(error) &&
    error.every(
      (item) =>
        typeof item === 'object' &&
        'EdgeResponseStatus' in item &&
        typeof item.EdgeResponseStatus === 'number' &&
        'CustomStatusMessage' in item &&
        typeof item.CustomStatusMessage === 'string',
    )
  );
};

const hasHumanReadableMessage = (error: unknown): error is { message: string } => {
  if (!error) return false;
  const message = (error as any).message;
  return typeof message === 'string' && message.length !== 0;
};

const hasNestedError = (error: unknown): error is { error: unknown } =>
  !!error && typeof (error as any).error === 'object';

/**
 * Ensures only string are converted to error text
 */
const asExtractedText = (text: string): ExtractedErrorText => text as ExtractedErrorText;

export function extractError(error: unknown, defaultMessage = 'Error'): ExtractedErrorText {
  if (!error) return asExtractedText(defaultMessage);
  if (isCloudflareError(error) && error.at(0)?.CustomStatusMessage !== undefined)
    return asExtractedText(error.at(0)!.CustomStatusMessage);
  if (Array.isArray(error)) {
    const extractedErrors = error.map((e) => extractError(e, UNKNOWN_ERROR_MESSAGE));
    if (extractedErrors.some((e) => e !== UNKNOWN_ERROR_MESSAGE)) return asExtractedText(extractedErrors.join('\n'));
    return asExtractedText(defaultMessage);
  }
  if (typeof error === 'string' && error.length) return asExtractedText(error);
  if (hasNestedError(error)) return extractError(error.error, defaultMessage);
  if (hasHumanReadableMessage(error)) return asExtractedText(error.message);
  if (hasErrorString(error)) return asExtractedText(error.error);
  return asExtractedText(defaultMessage);
}
